import type { SupportedCurrency } from '@plevits/types';
import { SUPPORTED_CURRENCIES } from './currency-config';
import { CANONICAL_BASE, SITE_NAME } from './seo';

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: CANONICAL_BASE,
    logo: `${CANONICAL_BASE}/logo.svg`,
    description:
      'SaaS health analytics platform that connects to Stripe to provide founders with real-time business health, growth metrics, and estimated valuation.',
  };
}

export function softwareApplicationSchema(currency: SupportedCurrency = 'USD') {
  const config = SUPPORTED_CURRENCIES[currency];
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: CANONICAL_BASE,
    offers: [
      {
        '@type': 'Offer',
        name: 'Free',
        price: '0',
        priceCurrency: config.code,
      },
      {
        '@type': 'Offer',
        name: 'Founder',
        price: String(config.monthly),
        priceCurrency: config.code,
        priceValidUntil: new Date(
          new Date().getFullYear() + 1,
          0,
          1
        ).toISOString().split('T')[0],
        billingIncrement: 'P1M',
      },
    ],
  };
}

export function faqSchema(
  questions: Array<{ question: string; answer: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };
}

export function breadcrumbSchema(
  items: Array<{ name: string; path: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${CANONICAL_BASE}${item.path}`,
    })),
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: CANONICAL_BASE,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${CANONICAL_BASE}/directory?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function howToSchema(steps: Array<{ name: string; text: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to get your SaaS health score',
    description:
      'Connect your Stripe account to Plevits and get your SaaS health score, metrics, and valuation estimate in under 2 minutes.',
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export function itemListSchema(
  items: Array<{ name: string; slug: string; position: number }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      url: `${CANONICAL_BASE}/directory/${item.slug}`,
      name: item.name,
    })),
  };
}

export function articleSchema(article: {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `${CANONICAL_BASE}${article.path}`,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: organizationSchema(),
    publisher: organizationSchema(),
  };
}

export function profilePageSchema(profile: {
  companyName: string;
  slug: string;
  description?: string;
  industry?: string;
  healthScore?: number;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Organization',
      name: profile.companyName,
      url: `${CANONICAL_BASE}/directory/${profile.slug}`,
      ...(profile.description && { description: profile.description }),
      ...(profile.industry && { industry: profile.industry }),
    },
  };
}
